import { z } from 'zod';
import { ESTADO_INICIAL, sanitizarItens, type CartState } from '@/features/cart/cartReducer';

/**
 * Persistência do carrinho — F4.3.2.
 *
 * DUAS REGRAS INEGOCIÁVEIS:
 *
 * 1. Só gravamos `exameSlug`, `quantidade`, `unidadeSlug` e `cupom`.
 *    NUNCA preço. Preço gravado no navegador é preço congelado: o cliente volta
 *    quatro dias depois, a tabela mudou, e ele vê um valor na tela e outro na
 *    fatura. O preço é sempre derivado do catálogo vigente.
 *
 * 2. Nada aqui pode lançar. Armazenamento local falha de formas banais — cota
 *    cheia, navegação privada, cookies bloqueados por política corporativa,
 *    `localStorage` desabilitado. Nenhuma delas pode derrubar a loja: sem
 *    persistência o carrinho vira carrinho de memória, e o cliente que não
 *    fechar a aba compra normalmente.
 */

/** Chave versionada: mudou o formato, muda o `v`, e o carrinho velho é ignorado. */
export const CART_KEY = 'lsi.cart.v1';

/**
 * Validade de 7 dias. Depois disso o preço e a disponibilidade do catálogo já
 * mudaram o bastante para que restaurar seja pior do que começar limpo.
 */
export const CART_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const itemPersistidoSchema = z.object({
  exameSlug: z.string().min(1),
  quantidade: z.number().int().min(1),
});

/**
 * Note que não existe campo de preço neste esquema, e é de propósito: se algum
 * dia alguém gravar preço, o `safeParse` não vai reclamar, mas `carregar`
 * devolve apenas os campos abaixo — o preço morre na fronteira.
 */
const cartPersistidoSchema = z.object({
  salvoEm: z.number().int().positive(),
  itens: z.array(itemPersistidoSchema).default([]),
  unidadeSlug: z.string().nullable().default(null),
  cupom: z.string().nullable().default(null),
});

/**
 * Devolve o armazenamento ou `null`, sem nunca lançar.
 * O simples ACESSO a `localStorage` já lança `SecurityError` quando o navegador
 * bloqueia armazenamento de terceiros — por isso o acesso está no try.
 */
function deposito(): Storage | null {
  try {
    const store = globalThis.localStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

export function salvar(estado: CartState): void {
  const store = deposito();
  if (!store) return;

  try {
    const conteudo = {
      salvoEm: Date.now(),
      // Projeção explícita: só estes campos atravessam. Nada de espalhar o
      // estado inteiro, que amanhã pode ganhar um campo derivado de preço.
      itens: estado.itens.map(({ exameSlug, quantidade }) => ({ exameSlug, quantidade })),
      unidadeSlug: estado.unidadeSlug,
      cupom: estado.cupom,
    };
    store.setItem(CART_KEY, JSON.stringify(conteudo));
  } catch {
    // Cota estourada ou modo privado. O carrinho em memória segue válido.
  }
}

/**
 * Lê o carrinho persistido. Devolve `null` quando não há nada utilizável —
 * ausente, corrompido, fora do formato ou vencido.
 *
 * Os itens devolvidos ainda NÃO foram conferidos contra o catálogo. Quem faz
 * isso é `useCartHydration`, que precisa do catálogo carregado.
 */
export function carregar(): CartState | null {
  const store = deposito();
  if (!store) return null;

  let bruto: string | null;
  try {
    bruto = store.getItem(CART_KEY);
  } catch {
    return null;
  }
  if (!bruto) return null;

  let json: unknown;
  try {
    json = JSON.parse(bruto);
  } catch {
    // JSON truncado por escrita interrompida. Descarta em vez de insistir.
    limpar();
    return null;
  }

  const analise = cartPersistidoSchema.safeParse(json);
  if (!analise.success) {
    limpar();
    return null;
  }

  const { salvoEm, itens, unidadeSlug, cupom } = analise.data;

  if (Date.now() - salvoEm > CART_TTL_MS) {
    limpar();
    return null;
  }

  return {
    ...ESTADO_INICIAL,
    itens: sanitizarItens(itens),
    unidadeSlug,
    cupom,
  };
}

export function limpar(): void {
  const store = deposito();
  if (!store) return;

  try {
    store.removeItem(CART_KEY);
  } catch {
    // Sem armazenamento não há o que limpar.
  }
}
